/**
 * 방 선택 화면 — v2 문서 버전.
 *
 * v1 로도 화면 자체는 그릴 수 있었다(`buildRoomPickFlow`). 담기지 않던 것은 **고른
 * 다음에 일어나는 일** 이다. 카드를 누르면 이 화면에 답이 채워지는 게 아니라 짐 기록
 * 화면이 열리고, 그게 끝나야 방 하나가 완료된다. 그 연결이 지금까지 어댑터의
 * `onRequestNavigation` 콜백 안에만 있었다.
 *
 * 여기서는 스텝 두 개로 적는다: 고르는 화면, 그리고 고른 방의 짐 기록으로 넘기는 자리.
 * 짐 기록이 끝나면 문서 전체가 끝나고, 상담이 갱신되면 규칙이 다시 이 화면을 띄운다
 * — 남은 방이 하나 줄어든 채로.
 */
import type { AssetDocument, DataSource } from "@miso/design-system";

import { STAGE_DOCUMENT_IDS } from "./counselFullFlow";

/** 호스트가 등록해야 하는 이름. 상담 스냅샷의 모양은 호스트마다 달라 여기서 모른다. */
export const ROOM_PICK_REFS = {
  /** 방 목록. 프로덕션은 sketching 세션에서, 프리뷰는 목 상담에서 만든다. */
  entries: "counsel.roomEntries",
  sections: "roomPick.sections",
  doneInstances: "roomPick.doneInstances",
} as const;

export const ROOM_PICK_DATA: DataSource[] = [
  { id: "entries", source: ROOM_PICK_REFS.entries },
  {
    id: "sections",
    derive: ROOM_PICK_REFS.sections,
    args: { entries: { $var: "entries" } },
  },
  // 완료된 방은 카드에 배지가 붙는다. picker 는 "인스턴스가 있는 항목" 을 완료로 본다.
  {
    id: "roomToRecord",
    derive: ROOM_PICK_REFS.doneInstances,
    args: { entries: { $var: "entries" } },
  },
];

const doc: AssetDocument = {
  id: STAGE_DOCUMENT_IDS.pickRoom,
  schemaVersion: 2,
  initial: "pick",
  data: ROOM_PICK_DATA,
  steps: {
    pick: {
      kind: "screen",
      layout: {
        type: "Screen",
        props: { variant: "question", title: "기록할 방을 선택해주세요." },
        children: [
          {
            type: "ItemPicker",
            bind: "roomToRecord",
            props: {
              sections: { $var: "sections" },
              // 고르면 답이 채워지는 게 아니라 다른 화면이 열린다.
              mode: "navigation",
              multiple: false,
              requireAllItems: true,
              reviewTitle: "기록할 방",
              reviewButtonLabel: "완료",
              // 프로덕션은 CTA 아래에 방 목록을 고치는 텍스트 버튼을 둔다.
              footerActionLabel: "방 수정하기",
            },
            on: {
              /*
               * 누른 방을 스코프에 적고 짐 기록으로 넘어간다. `$event` 는 자산이 넘긴
               * 항목이다 — 어느 카드를 눌렀는지는 반복 스코프로 정해지지 않는다.
               */
              onItemPress: [
                {
                  do: "setValues",
                  values: {
                    targetRoomId: { $var: "$event.id" },
                    roomLabel: { $var: "$event.title" },
                    roomType: { $var: "$event.roomType" },
                  },
                },
                { do: "goto", step: "record" },
              ],
              onFooterAction: [{ do: "goto", step: "editRooms" }],
            },
          },
        ],
      },
      next: { kind: "exit" },
    },

    /*
     * 고른 방의 짐 기록. 화면은 짐 목록 문서가 그린다 — 현관 자동 진행에서 뜨는 것과
     * 같은 문서다. 방마다 다른 화면을 두면 두 벌이 갈라진다.
     */
    record: {
      kind: "transform",
      code: "return {};",
      outputKey: "_record",
      next: { kind: "chain", target: STAGE_DOCUMENT_IDS.movingItems },
    },

    /** 방 수정 — 집 구조 완료 문서의 수정 화면을 그대로 연다. */
    editRooms: {
      kind: "transform",
      code: "return { __enter: 'edit' };",
      outputKey: "_edit",
      next: { kind: "chain", target: STAGE_DOCUMENT_IDS.houseStructureDone },
    },
  },
};

export const PICK_ROOM = { doc };
